/**
 * Advanced Inventory Search Component
 *
 * Provides comprehensive search capabilities with all filters supported by the backend
 */

import {
  CalendarIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  TagIcon,
  XIcon,
} from '@phosphor-icons/react';
import React, { useCallback, useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { InventorySearchConfig } from '@/types/inventory';

interface AdvancedSearchProps {
  onSearch: (config: InventorySearchConfig) => void;
  onClear?: () => void;
  loading?: boolean;
  suppliers?: Array<{ _id: string; businessName: string }>;
  categories?: string[];
  initialFilters?: InventorySearchConfig;
  className?: string;
}

const QUALITY_GRADES = [
  { value: 'A', label: 'Grade A' },
  { value: 'B', label: 'Grade B' },
  { value: 'C', label: 'Grade C' },
  { value: 'Premium', label: 'Premium' },
  { value: 'Rejected', label: 'Rejected' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'discontinued', label: 'Discontinued' },
  { value: 'out_of_stock', label: 'Out of Stock' },
];

const APPROVAL_STATUS_OPTIONS = [
  { value: 'approved', label: 'Approved' },
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'createdAt', label: 'Date Created' },
  { value: 'updatedAt', label: 'Last Updated' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'price', label: 'Price' },
  { value: 'category', label: 'Category' },
];

const PRICE_FIELD_OPTIONS = [
  { value: 'sellingPrice', label: 'Selling Price' },
  { value: 'costPrice', label: 'Cost Price' },
  { value: 'wholesalePrice', label: 'Wholesale Price' },
  { value: 'retailPrice', label: 'Retail Price' },
];

const DATE_FIELD_OPTIONS = [
  { value: 'createdAt', label: 'Date Created' },
  { value: 'updatedAt', label: 'Last Updated' },
  { value: 'lastModified', label: 'Last Modified' },
];

export const InventoryAdvancedSearch: React.FC<AdvancedSearchProps> = ({
  onSearch,
  onClear,
  loading = false,
  suppliers = [],
  categories = [],
  initialFilters,
  className = '',
}) => {
  // Search state
  const [searchText, setSearchText] = useState(initialFilters?.search || '');

  // Category filters
  const [primaryCategory, setPrimaryCategory] = useState(
    initialFilters?.category?.primary || ''
  );
  const [secondaryCategory, setSecondaryCategory] = useState(
    initialFilters?.category?.secondary || ''
  );
  const [subcategory, setSubcategory] = useState(
    initialFilters?.category?.subcategory || ''
  );

  // Stock level filters
  const [stockMin, setStockMin] = useState<number | undefined>(
    initialFilters?.stockLevel?.min
  );
  const [stockMax, setStockMax] = useState<number | undefined>(
    initialFilters?.stockLevel?.max
  );
  const [includeOutOfStock, setIncludeOutOfStock] = useState(
    initialFilters?.stockLevel?.includeOutOfStock || false
  );
  const [lowStockOnly, setLowStockOnly] = useState(
    initialFilters?.stockLevel?.lowStockOnly || false
  );
  const [availableOnly, setAvailableOnly] = useState(
    initialFilters?.stockLevel?.availableOnly || true
  );

  // Price range filters
  const [priceMin, setPriceMin] = useState<number | undefined>(
    initialFilters?.priceRange?.min
  );
  const [priceMax, setPriceMax] = useState<number | undefined>(
    initialFilters?.priceRange?.max
  );
  const [priceField, setPriceField] = useState<
    'costPrice' | 'sellingPrice' | 'wholesalePrice' | 'retailPrice'
  >(
    (initialFilters?.priceRange?.field as
      | 'costPrice'
      | 'sellingPrice'
      | 'wholesalePrice'
      | 'retailPrice') || 'sellingPrice'
  );

  // Status filters
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    initialFilters?.status || []
  );
  const [selectedApprovalStatuses, setSelectedApprovalStatuses] = useState<
    string[]
  >(initialFilters?.approvalStatus || []);

  // Supplier filters
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>(
    initialFilters?.suppliers || []
  );

  // Quality filters
  const [selectedQualityGrades, setSelectedQualityGrades] = useState<string[]>(
    initialFilters?.qualityGrade || []
  );

  // Date filters
  const [dateField, setDateField] = useState<
    'createdAt' | 'updatedAt' | 'lastModified'
  >(
    (initialFilters?.dateRange?.field as
      | 'createdAt'
      | 'updatedAt'
      | 'lastModified') || 'createdAt'
  );
  const [dateStart, setDateStart] = useState(
    initialFilters?.dateRange?.start || ''
  );
  const [dateEnd, setDateEnd] = useState(initialFilters?.dateRange?.end || '');

  // Advanced filters
  const [hasVariants, setHasVariants] = useState<boolean | null>(
    initialFilters?.hasVariants ?? null
  );
  const [batchTrackingEnabled, setBatchTrackingEnabled] = useState(
    initialFilters?.batchTrackingEnabled || false
  );
  const [autoReorderEnabled, setAutoReorderEnabled] = useState(
    initialFilters?.autoReorderEnabled || false
  );
  const [expiringBatches, setExpiringBatches] = useState(
    initialFilters?.expiringBatches || false
  );

  // Sorting
  const [sortBy, setSortBy] = useState(initialFilters?.sortBy || 'name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    initialFilters?.sortOrder || 'asc'
  );

  // Additional options
  const [includeAnalytics, setIncludeAnalytics] = useState(
    initialFilters?.includeAnalytics || false
  );
  const [includeSupplierDetails, setIncludeSupplierDetails] = useState(
    initialFilters?.includeSupplierDetails || false
  );
  const [includeVariants, setIncludeVariants] = useState(
    initialFilters?.includeVariants || false
  );

  // UI state
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  // Build search configuration
  const buildSearchConfig = useCallback((): InventorySearchConfig => {
    const config: InventorySearchConfig = {};

    // Text search
    if (searchText.trim()) {
      config.search = searchText.trim();
    }

    // Category filters
    if (primaryCategory || secondaryCategory || subcategory) {
      config.category = {};
      if (primaryCategory) config.category.primary = primaryCategory;
      if (secondaryCategory) config.category.secondary = secondaryCategory;
      if (subcategory) config.category.subcategory = subcategory;
    }

    // Stock level filters
    if (
      stockMin !== undefined ||
      stockMax !== undefined ||
      includeOutOfStock ||
      lowStockOnly ||
      !availableOnly
    ) {
      config.stockLevel = {};
      if (stockMin !== undefined) config.stockLevel.min = stockMin;
      if (stockMax !== undefined) config.stockLevel.max = stockMax;
      config.stockLevel.includeOutOfStock = includeOutOfStock;
      config.stockLevel.lowStockOnly = lowStockOnly;
      config.stockLevel.availableOnly = availableOnly;
    }

    // Price range filters
    if (priceMin !== undefined || priceMax !== undefined) {
      config.priceRange = {
        field: priceField,
      };
      if (priceMin !== undefined) config.priceRange.min = priceMin;
      if (priceMax !== undefined) config.priceRange.max = priceMax;
    }

    // Status filters
    if (selectedStatuses.length > 0) {
      config.status = selectedStatuses;
    }

    if (selectedApprovalStatuses.length > 0) {
      config.approvalStatus = selectedApprovalStatuses;
    }

    // Supplier filters
    if (selectedSuppliers.length > 0) {
      config.suppliers = selectedSuppliers;
    }

    // Quality filters
    if (selectedQualityGrades.length > 0) {
      config.qualityGrade = selectedQualityGrades;
    }

    // Date filters
    if (dateStart || dateEnd) {
      config.dateRange = {
        field: dateField,
      };
      if (dateStart) config.dateRange.start = dateStart;
      if (dateEnd) config.dateRange.end = dateEnd;
    }

    // Advanced filters
    if (hasVariants !== null) {
      config.hasVariants = hasVariants;
    }

    config.batchTrackingEnabled = batchTrackingEnabled;
    config.autoReorderEnabled = autoReorderEnabled;
    config.expiringBatches = expiringBatches;

    // Sorting
    config.sortBy = sortBy;
    config.sortOrder = sortOrder;

    // Additional options
    config.includeAnalytics = includeAnalytics;
    config.includeSupplierDetails = includeSupplierDetails;
    config.includeVariants = includeVariants;

    // Pagination (will be handled by the parent component)
    config.pagination = {
      page: 1,
      limit: 20,
    };

    return config;
  }, [
    searchText,
    primaryCategory,
    secondaryCategory,
    subcategory,
    stockMin,
    stockMax,
    includeOutOfStock,
    lowStockOnly,
    availableOnly,
    priceMin,
    priceMax,
    priceField,
    selectedStatuses,
    selectedApprovalStatuses,
    selectedSuppliers,
    selectedQualityGrades,
    dateField,
    dateStart,
    dateEnd,
    hasVariants,
    batchTrackingEnabled,
    autoReorderEnabled,
    expiringBatches,
    sortBy,
    sortOrder,
    includeAnalytics,
    includeSupplierDetails,
    includeVariants,
  ]);

  // Handle search
  const handleSearch = useCallback(() => {
    const config = buildSearchConfig();
    onSearch(config);
  }, [buildSearchConfig, onSearch]);

  // Handle clear
  const handleClear = useCallback(() => {
    // Reset all filters
    setSearchText('');
    setPrimaryCategory('');
    setSecondaryCategory('');
    setSubcategory('');
    setStockMin(undefined);
    setStockMax(undefined);
    setIncludeOutOfStock(false);
    setLowStockOnly(false);
    setAvailableOnly(true);
    setPriceMin(undefined);
    setPriceMax(undefined);
    setPriceField('sellingPrice');
    setSelectedStatuses([]);
    setSelectedApprovalStatuses([]);
    setSelectedSuppliers([]);
    setSelectedQualityGrades([]);
    setDateField('createdAt');
    setDateStart('');
    setDateEnd('');
    setHasVariants(null);
    setBatchTrackingEnabled(false);
    setAutoReorderEnabled(false);
    setExpiringBatches(false);
    setSortBy('name');
    setSortOrder('asc');
    setIncludeAnalytics(false);
    setIncludeSupplierDetails(false);
    setIncludeVariants(false);

    if (onClear) {
      onClear();
    } else {
      // If no onClear handler, trigger search with empty config
      onSearch({});
    }
  }, [onSearch, onClear]);

  // Calculate active filters
  useEffect(() => {
    const filters: string[] = [];

    if (searchText.trim()) filters.push('Search Text');
    if (primaryCategory) filters.push('Category');
    if (stockMin !== undefined || stockMax !== undefined)
      filters.push('Stock Range');
    if (includeOutOfStock) filters.push('Include Out of Stock');
    if (lowStockOnly) filters.push('Low Stock Only');
    if (!availableOnly) filters.push('Include Unavailable');
    if (priceMin !== undefined || priceMax !== undefined)
      filters.push('Price Range');
    if (selectedStatuses.length > 0) filters.push('Status');
    if (selectedApprovalStatuses.length > 0) filters.push('Approval Status');
    if (selectedSuppliers.length > 0) filters.push('Suppliers');
    if (selectedQualityGrades.length > 0) filters.push('Quality Grades');
    if (dateStart || dateEnd) filters.push('Date Range');
    if (hasVariants !== null) filters.push('Product Type');
    if (batchTrackingEnabled) filters.push('Batch Tracking');
    if (autoReorderEnabled) filters.push('Auto Reorder');
    if (expiringBatches) filters.push('Expiring Batches');
    if (sortBy !== 'name' || sortOrder !== 'asc') filters.push('Sorting');

    setActiveFilters(filters);
  }, [
    searchText,
    primaryCategory,
    stockMin,
    stockMax,
    includeOutOfStock,
    lowStockOnly,
    availableOnly,
    priceMin,
    priceMax,
    selectedStatuses,
    selectedApprovalStatuses,
    selectedSuppliers,
    selectedQualityGrades,
    dateStart,
    dateEnd,
    hasVariants,
    batchTrackingEnabled,
    autoReorderEnabled,
    expiringBatches,
    sortBy,
    sortOrder,
  ]);

  // Handle array toggle
  const toggleArrayValue = useCallback(
    (value: string, array: string[], setter: (arr: string[]) => void) => {
      if (array.includes(value)) {
        setter(array.filter(item => item !== value));
      } else {
        setter([...array, value]);
      }
    },
    []
  );

  return (
    <Card className={`w-full ${className}`}>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <MagnifyingGlassIcon className='h-5 w-5 text-gray-60' />
            <CardTitle>Advanced Search & Filters</CardTitle>
            {activeFilters.length > 0 && (
              <Badge variant='secondary' className='ml-2'>
                {activeFilters.length} active
              </Badge>
            )}
          </div>
          <div className='flex items-center gap-2'>
            <Button
              hierarchy='outlined'
              size='sm'
              onClick={handleClear}
              disabled={loading}
              className='text-gray-60 hover:text-gray-90'
            >
              <XIcon className='h-4 w-4' />
              Clear
            </Button>
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <CollapsibleTrigger asChild>
                <Button hierarchy='outlined' size='sm'>
                  <FunnelIcon className='h-4 w-4' />
                  {isExpanded ? 'Less Filters' : 'More Filters'}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className='flex flex-wrap gap-2 mt-3'>
            {activeFilters.map((filter, index) => (
              <Badge key={index} variant='outline' className='text-xs'>
                {filter}
              </Badge>
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className='space-y-6'>
        {/* Basic Search */}
        <div className='space-y-2'>
          <Label htmlFor='search-text'>Search Products</Label>
          <div className='relative'>
            <MagnifyingGlassIcon className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-60' />
            <Input
              id='search-text'
              placeholder='Search by name, SKU, description...'
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className='pl-10'
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        {/* Quick Filters */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div className='space-y-2'>
            <Label>Primary Category</Label>
            <Select value={primaryCategory} onValueChange={setPrimaryCategory}>
              <SelectTrigger>
                <SelectValue placeholder='All categories' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=''>All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label>Stock Status</Label>
            <div className='flex flex-wrap gap-2'>
              <div className='flex items-center space-x-2'>
                <Switch
                  id='low-stock-only'
                  checked={lowStockOnly}
                  onCheckedChange={setLowStockOnly}
                />
                <Label htmlFor='low-stock-only' className='text-sm'>
                  Low Stock
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <Switch
                  id='include-out-of-stock'
                  checked={includeOutOfStock}
                  onCheckedChange={setIncludeOutOfStock}
                />
                <Label htmlFor='include-out-of-stock' className='text-sm'>
                  Out of Stock
                </Label>
              </div>
            </div>
          </div>

          <div className='space-y-2'>
            <Label>Sort By</Label>
            <div className='flex gap-2'>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className='flex-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={sortOrder}
                onValueChange={value => setSortOrder(value as 'asc' | 'desc')}
              >
                <SelectTrigger className='w-20'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='asc'>↑</SelectItem>
                  <SelectItem value='desc'>↓</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleContent className='space-y-6'>
            <Separator />

            {/* Stock Range */}
            <div className='space-y-3'>
              <Label className='flex items-center gap-2'>
                <FunnelIcon className='h-4 w-4' />
                Stock Quantity Range
              </Label>
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='stock-min'>Minimum Quantity</Label>
                  <Input
                    id='stock-min'
                    type='number'
                    placeholder='0'
                    value={stockMin || ''}
                    onChange={e =>
                      setStockMin(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='stock-max'>Maximum Quantity</Label>
                  <Input
                    id='stock-max'
                    type='number'
                    placeholder='1000'
                    value={stockMax || ''}
                    onChange={e =>
                      setStockMax(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                  />
                </div>
              </div>
            </div>

            {/* Price Range */}
            <div className='space-y-3'>
              <Label className='flex items-center gap-2'>
                <CurrencyDollarIcon className='h-4 w-4' />
                Price Range
              </Label>
              <div className='grid grid-cols-3 gap-4'>
                <div className='space-y-2'>
                  <Label>Price Field</Label>
                  <Select
                    value={priceField}
                    onValueChange={value =>
                      setPriceField(
                        value as
                          | 'costPrice'
                          | 'sellingPrice'
                          | 'wholesalePrice'
                          | 'retailPrice'
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRICE_FIELD_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='price-min'>Minimum Price</Label>
                  <Input
                    id='price-min'
                    type='number'
                    step='0.01'
                    placeholder='0.00'
                    value={priceMin || ''}
                    onChange={e =>
                      setPriceMin(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='price-max'>Maximum Price</Label>
                  <Input
                    id='price-max'
                    type='number'
                    step='0.01'
                    placeholder='1000.00'
                    value={priceMax || ''}
                    onChange={e =>
                      setPriceMax(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                  />
                </div>
              </div>
            </div>

            {/* Status Filters */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div className='space-y-3'>
                <Label className='flex items-center gap-2'>
                  <CheckCircleIcon className='h-4 w-4' />
                  Item Status
                </Label>
                <div className='grid grid-cols-2 gap-2'>
                  {STATUS_OPTIONS.map(status => (
                    <div
                      key={status.value}
                      className='flex items-center space-x-2'
                    >
                      <Switch
                        id={`status-${status.value}`}
                        checked={selectedStatuses.includes(status.value)}
                        onCheckedChange={() =>
                          toggleArrayValue(
                            status.value,
                            selectedStatuses,
                            setSelectedStatuses
                          )
                        }
                      />
                      <Label
                        htmlFor={`status-${status.value}`}
                        className='text-sm'
                      >
                        {status.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className='space-y-3'>
                <Label>Approval Status</Label>
                <div className='grid grid-cols-2 gap-2'>
                  {APPROVAL_STATUS_OPTIONS.map(status => (
                    <div
                      key={status.value}
                      className='flex items-center space-x-2'
                    >
                      <Switch
                        id={`approval-${status.value}`}
                        checked={selectedApprovalStatuses.includes(
                          status.value
                        )}
                        onCheckedChange={() =>
                          toggleArrayValue(
                            status.value,
                            selectedApprovalStatuses,
                            setSelectedApprovalStatuses
                          )
                        }
                      />
                      <Label
                        htmlFor={`approval-${status.value}`}
                        className='text-sm'
                      >
                        {status.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quality Grades */}
            <div className='space-y-3'>
              <Label className='flex items-center gap-2'>
                <TagIcon className='h-4 w-4' />
                Quality Grades
              </Label>
              <div className='grid grid-cols-3 md:grid-cols-5 gap-2'>
                {QUALITY_GRADES.map(grade => (
                  <div
                    key={grade.value}
                    className='flex items-center space-x-2'
                  >
                    <Switch
                      id={`grade-${grade.value}`}
                      checked={selectedQualityGrades.includes(grade.value)}
                      onCheckedChange={() =>
                        toggleArrayValue(
                          grade.value,
                          selectedQualityGrades,
                          setSelectedQualityGrades
                        )
                      }
                    />
                    <Label htmlFor={`grade-${grade.value}`} className='text-sm'>
                      {grade.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className='space-y-3'>
              <Label className='flex items-center gap-2'>
                <CalendarIcon className='h-4 w-4' />
                Date Range
              </Label>
              <div className='grid grid-cols-3 gap-4'>
                <div className='space-y-2'>
                  <Label>Date Field</Label>
                  <Select
                    value={dateField}
                    onValueChange={value =>
                      setDateField(
                        value as 'createdAt' | 'updatedAt' | 'lastModified'
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_FIELD_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='date-start'>Start Date</Label>
                  <Input
                    id='date-start'
                    type='date'
                    value={dateStart}
                    onChange={e => setDateStart(e.target.value)}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='date-end'>End Date</Label>
                  <Input
                    id='date-end'
                    type='date'
                    value={dateEnd}
                    onChange={e => setDateEnd(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Advanced Options */}
            <div className='space-y-3'>
              <Label>Advanced Options</Label>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                <div className='flex items-center space-x-2'>
                  <Switch
                    id='has-variants-switch'
                    checked={hasVariants === true}
                    onCheckedChange={checked =>
                      setHasVariants(checked ? true : null)
                    }
                  />
                  <Label htmlFor='has-variants-switch' className='text-sm'>
                    Has Variants
                  </Label>
                </div>

                <div className='flex items-center space-x-2'>
                  <Switch
                    id='batch-tracking-switch'
                    checked={batchTrackingEnabled}
                    onCheckedChange={setBatchTrackingEnabled}
                  />
                  <Label htmlFor='batch-tracking-switch' className='text-sm'>
                    Batch Tracking
                  </Label>
                </div>

                <div className='flex items-center space-x-2'>
                  <Switch
                    id='auto-reorder-switch'
                    checked={autoReorderEnabled}
                    onCheckedChange={setAutoReorderEnabled}
                  />
                  <Label htmlFor='auto-reorder-switch' className='text-sm'>
                    Auto Reorder
                  </Label>
                </div>

                <div className='flex items-center space-x-2'>
                  <Switch
                    id='expiring-batches-switch'
                    checked={expiringBatches}
                    onCheckedChange={setExpiringBatches}
                  />
                  <Label htmlFor='expiring-batches-switch' className='text-sm'>
                    Expiring Batches
                  </Label>
                </div>

                <div className='flex items-center space-x-2'>
                  <Switch
                    id='include-analytics-switch'
                    checked={includeAnalytics}
                    onCheckedChange={setIncludeAnalytics}
                  />
                  <Label htmlFor='include-analytics-switch' className='text-sm'>
                    Include Analytics
                  </Label>
                </div>

                <div className='flex items-center space-x-2'>
                  <Switch
                    id='include-supplier-details-switch'
                    checked={includeSupplierDetails}
                    onCheckedChange={setIncludeSupplierDetails}
                  />
                  <Label
                    htmlFor='include-supplier-details-switch'
                    className='text-sm'
                  >
                    Supplier Details
                  </Label>
                </div>
              </div>
            </div>

            {/* Suppliers Filter (if suppliers are provided) */}
            {suppliers.length > 0 && (
              <div className='space-y-3'>
                <Label>Filter by Suppliers</Label>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-y-auto border rounded-md p-3'>
                  {suppliers.map(supplier => (
                    <div
                      key={supplier._id}
                      className='flex items-center space-x-2'
                    >
                      <Switch
                        id={`supplier-${supplier._id}`}
                        checked={selectedSuppliers.includes(supplier._id)}
                        onCheckedChange={() =>
                          toggleArrayValue(
                            supplier._id,
                            selectedSuppliers,
                            setSelectedSuppliers
                          )
                        }
                      />
                      <Label
                        htmlFor={`supplier-${supplier._id}`}
                        className='text-sm truncate'
                      >
                        {supplier.businessName}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Action Buttons */}
        <div className='flex justify-between pt-4 border-t'>
          <div className='text-sm text-gray-60'>
            {activeFilters.length > 0
              ? `${activeFilters.length} filters applied`
              : 'No filters applied'}
          </div>
          <div className='flex gap-2'>
            <Button
              hierarchy='outlined'
              onClick={handleClear}
              disabled={loading || activeFilters.length === 0}
            >
              Reset All
            </Button>
            <Button
              onClick={handleSearch}
              disabled={loading}
              className='min-w-24'
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InventoryAdvancedSearch;
